/**
 * Remind-me-later calendar file (variants block plan 3, Step 8 / D10).
 *
 * THREAD's pre-share exit ramp: someone who isn't ready to swap contacts in
 * the moment gets a calendar event carrying the permanent room link, so the
 * exchange survives them walking away. The OS calendar sheet opening IS the
 * feedback — same reasoning as [Text this link] and [Save to phone], both of
 * which retire on tap.
 *
 * Event content per D10a (user ruling 2026-08-15 — none of this was in the
 * mocks, because the ICS is the one artifact that renders outside the app):
 *
 *   WHEN   the next morning at 10:00 LOCAL. Out of the live moment but inside
 *          the same memory window. Written as a floating (zoneless) local time
 *          so it means 10am wherever the person wakes up, not 10am in whatever
 *          timezone they happened to scan in.
 *   TITLE  "Continue chat with {FirstName}" (user copy 2026-08-16; "Finish
 *          connecting with…" → "Resume amazing chat with…" → this). It is
 *          literally true — the room already exists and the host's card is in
 *          it, so they are continuing a thread rather than starting one — and
 *          it stays plain, which matters for a line that renders in someone's
 *          personal calendar among real appointments. Falls back to "Continue
 *          chat on Royale" when the name hasn't landed yet (the host's first
 *          name normally arrives with the formation auto-response, but the
 *          reminder can be tapped first). Degraded copy, not a data fallback.
 *   ALARM  fires AT the event. A calendar entry with no alert reminds nobody —
 *          without this the whole feature is decorative.
 *   LINK   in the event body, never the title, which has to stay readable at a
 *          glance in a month view.
 *
 * The room link must already carry the variant tag (getVariantQrTag) so the
 * return lands back in THREAD — the caller owns that; this module never
 * resolves the variant.
 *
 * SECURITY (prep § Security): the link is app-built, but peer-supplied text
 * (the host's name) is folded into the file, and in ICS grammar `,` `;` and
 * `\` are delimiters — an unescaped name could otherwise inject properties.
 * escapeIcsText handles it and is unit-tested with those characters.
 */

/** RFC 5545 §3.3.11 TEXT escaping. Backslash FIRST or it double-escapes. */
export const escapeIcsText = (value) =>
  String(value ?? '')
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    // Every line-break form, not /\r?\n/ — a LONE \r slipped through that one,
    // and some ICS parsers treat bare CR as a line terminator (so it could end
    // a property early). Unreachable from firstName today, which firstNameOf
    // has already split on whitespace; closed anyway because the cost is a
    // character and the cost of being wrong is a malformed calendar entry
    // (/security-review sub-threshold note, 2026-08-17).
    .replace(/\r\n|\r|\n/g, '\\n');

/**
 * RFC 5545 §3.1 content-line folding: lines cap at 75 octets, continuations
 * begin with a single space. Folded on characters — the app's own strings are
 * ASCII, and splitting a multi-byte character across a fold is the one thing
 * this must not do, so the limit stays conservative.
 */
export const foldIcsLine = (line) => {
  if (line.length <= 73) return line;
  const parts = [line.slice(0, 73)];
  let rest = line.slice(73);
  while (rest.length > 72) {
    parts.push(` ${rest.slice(0, 72)}`);
    rest = rest.slice(72);
  }
  if (rest.length) parts.push(` ${rest}`);
  return parts.join('\r\n');
};

/** Local (floating) ICS timestamp: YYYYMMDDTHHMMSS, no trailing Z. */
const toFloatingStamp = (date) => {
  const pad = (n) => String(n).padStart(2, '0');
  return (
    `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}` +
    `T${pad(date.getHours())}${pad(date.getMinutes())}00`
  );
};

/** UTC stamp for DTSTAMP, which must be absolute. */
const toUtcStamp = (date) => `${date.toISOString().replace(/[-:]/g, '').split('.')[0]}Z`;

/**
 * The next morning at 10:00 local, relative to `from`.
 *
 * "Next morning" always means the following calendar day — scanning at 9am
 * still reminds you tomorrow, not in an hour. The deferral is about getting
 * out of the moment, and an hour later is still the moment.
 */
export const nextMorningAt10 = (from = new Date()) => {
  const d = new Date(from.getTime());
  d.setDate(d.getDate() + 1);
  d.setHours(10, 0, 0, 0);
  return d;
};

export const reminderTitle = (firstName) =>
  firstName ? `Continue chat with ${firstName}` : 'Continue chat on Royale';

/**
 * Build the .ics text.
 *
 * @param {Object} params
 * @param {string} [params.firstName] - host's first name, may be absent
 * @param {string} params.roomUrl - permanent room link, ALREADY variant-tagged
 * @param {Date}   [params.now] - injected for tests
 * @param {string} [params.uid] - injected for tests (else derived from the URL)
 * @returns {string} ICS file contents, CRLF-delimited
 */
export const buildReminderIcs = ({ firstName, roomUrl, now = new Date(), uid }) => {
  const start = nextMorningAt10(now);
  // 15 minutes: long enough to read as a real appointment, short enough not to
  // block out someone's morning.
  const end = new Date(start.getTime() + 15 * 60 * 1000);
  const title = reminderTitle(firstName);
  const description = `Hit this link to say hi and swap info:\n${roomUrl}`;

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Royale//Remind me later//EN',
    'CALSCALE:GREGORIAN',
    'BEGIN:VEVENT',
    `UID:${uid || `royale-${toUtcStamp(now)}-${encodeURIComponent(roomUrl).slice(-24)}`}`,
    `DTSTAMP:${toUtcStamp(now)}`,
    `DTSTART:${toFloatingStamp(start)}`,
    `DTEND:${toFloatingStamp(end)}`,
    `SUMMARY:${escapeIcsText(title)}`,
    `DESCRIPTION:${escapeIcsText(description)}`,
    `URL:${escapeIcsText(roomUrl)}`,
    'BEGIN:VALARM',
    'ACTION:DISPLAY',
    'TRIGGER:PT0M',
    `DESCRIPTION:${escapeIcsText(title)}`,
    'END:VALARM',
    'END:VEVENT',
    'END:VCALENDAR',
  ];

  return lines.map(foldIcsLine).join('\r\n');
};

/**
 * Hand the .ics to the OS. A Blob object URL on an <a download> is what opens
 * the calendar's "Add Event" sheet on iOS and the chooser on Android — the
 * platforms differ in what happens next, which is why the device read must not
 * assume iOS (logged edge case).
 */
export const openReminderIcs = ({ firstName, roomUrl, now }) => {
  const ics = buildReminderIcs({ firstName, roomUrl, now });
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = 'royale-reminder.ics';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);

  // Revoke late — revoking synchronously can cancel the hand-off on Safari.
  setTimeout(() => URL.revokeObjectURL(url), 10000);
};
